import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileText, Plus, RefreshCw, Filter, Edit2, Trash2,
  Send, Eye, CheckCircle2, XCircle, Clock, Printer, PackageCheck,
  ShoppingCart, X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { getDeviseCourante } from '../../utils/format';
import DateRangePicker from '../../components/common/DateRangePicker';
import { getPeriodRange } from '../../components/common/PeriodPills';
import FormBonCommande from './FormBonCommande';
import ModalImprimerBon from './ModalImprimerBon';
import FormApprovisionnement from '../fournisseurs/FormApprovisionnement';
import { useAuth } from '../../context/AuthContext';

function formatGNF(n) {
  return Number(n || 0).toLocaleString('fr-FR') + ' ' + getDeviseCourante();
}

// Statuts du bon — étiquettes et couleurs cohérentes avec le reste de l'app
const STATUTS = {
  brouillon: { label: 'Brouillon',  color: 'bg-gray-700 text-gray-200',    icon: Edit2 },
  envoye:    { label: 'Envoyé',     color: 'bg-blue-700/40 text-blue-300', icon: Send },
  recu:      { label: 'Reçu',       color: 'bg-green-700/40 text-green-300', icon: CheckCircle2 },
  annule:    { label: 'Annulé',     color: 'bg-red-700/40 text-red-300',   icon: XCircle },
};

function StatutBadge({ statut }) {
  const s = STATUTS[statut] || STATUTS.brouillon;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${s.color}`}>
      <Icon size={11} /> {s.label}
    </span>
  );
}

export default function BonsCommande() {
  const today = new Date().toISOString().split('T')[0];
  const { user } = useAuth();
  const peutCorriger = ['proprietaire', 'admin'].includes(user?.role);
  const [apprEdit, setApprEdit] = useState(null); // appro (avec lignes) à corriger
  const [expanded, setExpanded] = useState(null);     // clé de la ligne dépliée
  const [detailLignes, setDetailLignes] = useState({}); // cache { clé: lignes | 'loading' }
  const [bons, setBons]         = useState([]);
  const [appros, setAppros]     = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  // Filtre date par défaut = Aujourd'hui (avant : vide = tout). Évite
  // de charger des centaines de lignes au mount.
  const [dateDebut, setDateDebut] = useState(today);
  const [dateFin, setDateFin]     = useState(today);
  const [statut, setStatut]     = useState('tous');
  // Filtre type : 'tous' (commandes + appros directs) | 'commandes' | 'appros'
  const [typeFiltre, setTypeFiltre] = useState('tous');
  // Filtre fournisseur : '' (tous) ou UUID
  const [fournisseurFiltre, setFournisseurFiltre] = useState('');
  const [page, setPage]         = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modalCreer, setModalCreer] = useState(false);
  const [bonEdit, setBonEdit]   = useState(null); // bon à éditer (objet ou null)
  const [bonImpression, setBonImpression] = useState(null); // bon (avec lignes) à imprimer
  // Approvisionnement direct (sans bon préalable) — flux récupéré de la page
  // Fournisseurs. Étape 1 : choisir le fournisseur. Étape 2 : formulaire.
  const [modalApproDirect, setModalApproDirect] = useState(false);
  const [fournisseursActifs, setFournisseursActifs] = useState([]);
  const [fournisseurAppro, setFournisseurAppro] = useState(null);

  // Charger la liste des fournisseurs actifs au mount (utilisé par le modal
  // d'appro direct — sélection en 1 clic).
  useEffect(() => {
    api.get('/fournisseurs').then(r => {
      const arr = Array.isArray(r.data) ? r.data : (r.data.fournisseurs || []);
      setFournisseursActifs(arr.filter(f => f.statut === 'actif'));
    }).catch(() => {});
  }, []);

  // Charge en parallèle commandes + appros directs selon typeFiltre. Pour
  // simplifier la pagination, chaque source est fetchée séparément avec sa
  // propre pagination puis fusionnée. Le filtre statut ne s'applique qu'aux
  // commandes (les appros directs sont toujours 'reçu').
  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const tasks = [];

      // ── Source A : bons de commande
      if (typeFiltre === 'tous' || typeFiltre === 'commandes') {
        const params = new URLSearchParams({ page, limit: 50 });
        if (statut !== 'tous') params.append('statut', statut);
        if (dateDebut) params.append('date_debut', dateDebut);
        if (dateFin)   params.append('date_fin',   dateFin);
        if (fournisseurFiltre) params.append('fournisseur_id', fournisseurFiltre);
        tasks.push(api.get(`/bons-commande?${params}`).then(r => r.data));
      } else {
        tasks.push(Promise.resolve({ bons: [], total: 0, totalPages: 1 }));
      }

      // ── Source B : approvisionnements directs (filtrés sur via_bon=false)
      // Un appro direct = stock déjà entré → statut équivalent « Reçu ». On ne
      // l'affiche donc que si le filtre statut est « Tous » ou « Reçu » ;
      // sinon (Brouillon / Envoyé / Annulé) il ne doit pas apparaître.
      const approsVisibles = (statut === 'tous' || statut === 'recu');
      if ((typeFiltre === 'tous' || typeFiltre === 'appros') && approsVisibles) {
        const params = new URLSearchParams({ page, limit: 50 });
        if (dateDebut) params.append('date_debut', dateDebut);
        if (dateFin)   params.append('date_fin',   dateFin);
        if (fournisseurFiltre) params.append('fournisseur_id', fournisseurFiltre);
        tasks.push(
          api.get(`/fournisseurs/approvisionnements?${params}`)
            .then(r => r.data)
            .catch(() => ({ approvisionnements: [], total: 0, totalPages: 1 }))
        );
      } else {
        tasks.push(Promise.resolve({ approvisionnements: [], total: 0, totalPages: 1 }));
      }

      const [dataBons, dataAppros] = await Promise.all(tasks);
      setBons(dataBons.bons || []);
      // On masque les appros déjà rattachés à un bon (sinon doublon visuel
      // dans la vue "Tous" puisque la commande recu et l'appro pointent
      // sur la même opération économique).
      setAppros((dataAppros.approvisionnements || []).filter(a => !a.via_bon));
      // Total combiné — approximation : on additionne les totaux des deux
      // sources (la pagination reste par source). Suffisant pour affichage.
      const totalCombine = (dataBons.total || 0) + (dataAppros.total || 0);
      setTotal(totalCombine);
      setTotalPages(Math.max(dataBons.totalPages || 1, dataAppros.totalPages || 1));
    } catch {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [statut, dateDebut, dateFin, page, typeFiltre, fournisseurFiltre]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { setPage(1); }, [statut, dateDebut, dateFin, typeFiltre, fournisseurFiltre]);

  // Liste unifiée triée par date desc (date_emission pour commande, date
  // pour appro). Chaque item a _type pour distinguer les rendus & actions.
  const itemsAffiches = useMemo(() => {
    const aBons = bons.map(b => ({ ...b, _type: 'commande', _date: b.date_emission }));
    const aAppros = appros.map(a => ({
      ...a, _type: 'appro', _date: a.date,
      // Pour homogénéiser l'affichage dans le tableau :
      numero: `APP-${a.id.slice(0, 8).toUpperCase()}`,
      statut: 'recu', // appro = stock déjà entré
    }));
    return [...aBons, ...aAppros].sort((x, y) =>
      (y._date || '').localeCompare(x._date || '')
    );
  }, [bons, appros]);

  const refreshAll = () => {
    const t = getPeriodRange('today');
    setDateDebut(t.from); setDateFin(t.to);
    setStatut('tous');
    setTypeFiltre('tous');
    setFournisseurFiltre('');
  };

  const annuler = async (bon) => {
    if (!window.confirm(`Annuler le bon ${bon.numero} ?`)) return;
    try {
      await api.delete(`/bons-commande/${bon.id}`);
      toast.success('Bon annulé');
      charger();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    }
  };

  const marquerEnvoye = async (bon) => {
    if (!window.confirm(`Marquer le bon ${bon.numero} comme envoyé au fournisseur ?`)) return;
    try {
      await api.patch(`/bons-commande/${bon.id}/statut`, { statut: 'envoye' });
      toast.success('Bon marqué comme envoyé');
      charger();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    }
  };

  const marquerRecu = async (bon) => {
    if (!window.confirm(
      `Marquer le bon ${bon.numero} comme REÇU ?\n\n` +
      `Cela créera un approvisionnement avec les mêmes lignes :\n` +
      `• Stock mis à jour (+${bon.nb_articles} pièces)\n` +
      `• Caisse : sortie de ${Number(bon.cout_total).toLocaleString('fr-FR')} ${getDeviseCourante()}\n` +
      `• Prix d'achat moyen recalculé\n\n` +
      `Action irréversible.`
    )) return;
    try {
      const { data } = await api.post(`/bons-commande/${bon.id}/reception`);
      toast.success(`Reçu — Appro ${data.approvisionnement_id?.slice(0, 8)}… créé`);
      charger();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la conversion');
    }
  };

  // Ouvre le modal d'impression — on recharge le détail complet (avec lignes)
  // car la liste n'a pas les détails par défaut.
  const voirEtImprimer = async (bon) => {
    try {
      const { data } = await api.get(`/bons-commande/${bon.id}`);
      setBonImpression(data);
    } catch {
      toast.error('Erreur chargement du bon');
    }
  };

  // Ouvrir la correction d'un appro direct : on charge son détail (lignes)
  // puis on ouvre le formulaire en mode édition.
  const ouvrirCorrectionAppro = async (it) => {
    try {
      const { data } = await api.get(`/fournisseurs/approvisionnements/${it.id}`);
      setApprEdit({ ...data, fournisseur_nom: it.fournisseur_nom });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Impossible de charger l\'approvisionnement');
    }
  };

  // Déplier/replier le détail d'une ligne (chargé à la demande).
  const toggleDetail = async (it) => {
    const cle = `${it._type}-${it.id}`;
    if (expanded === cle) { setExpanded(null); return; }
    setExpanded(cle);
    if (detailLignes[cle] === undefined) {
      setDetailLignes(prev => ({ ...prev, [cle]: 'loading' }));
      try {
        const url = it._type === 'appro'
          ? `/fournisseurs/approvisionnements/${it.id}`
          : `/bons-commande/${it.id}`;
        const { data } = await api.get(url);
        setDetailLignes(prev => ({ ...prev, [cle]: data.lignes || [] }));
      } catch {
        setDetailLignes(prev => ({ ...prev, [cle]: [] }));
      }
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-white flex items-center gap-2">
            <FileText className="text-yellow-400" /> Approvisionnement / Commande
          </h1>
          <p className="text-gray-400 text-sm">{total} commande{total > 1 ? 's' : ''} au total</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={refreshAll}
            className="p-2.5 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-xl transition-all"
            title="Actualiser (remet à Aujourd'hui)">
            <RefreshCw size={16} />
          </button>
          <button onClick={() => setModalApproDirect(true)}
            className="flex items-center gap-2 bg-yellow-600 hover:bg-yellow-500 text-black font-bold px-4 py-2.5 rounded-xl transition-all"
            title="Approvisionnement direct (livraison déjà reçue, sans commande préalable)">
            <ShoppingCart size={18} /> Approvisionnement direct
          </button>
          <button onClick={() => { setBonEdit(null); setModalCreer(true); }}
            className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black font-bold px-4 py-2.5 rounded-xl transition-all">
            <Plus size={18} /> Nouvelle commande
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-2xl p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-gray-400" />
          <span className="text-xs text-gray-400 font-medium">Filtres</span>
        </div>

        {/* Type : Tous / Commandes / Approvisionnements directs */}
        <div className="flex gap-2 flex-wrap items-center">
          <span className="text-xs text-gray-500">Type :</span>
          {[
            { key: 'tous',      label: 'Tous' },
            { key: 'commandes', label: 'Commandes' },
            { key: 'appros',    label: 'Approvisionnements directs' },
          ].map(t => (
            <button key={t.key} onClick={() => {
                setTypeFiltre(t.key);
                // Un appro direct est toujours « Reçu » → on aligne le statut
                // pour que la liste s'affiche (sinon un statut Brouillon/Envoyé
                // resté actif masquerait tous les appros).
                if (t.key === 'appros') setStatut('recu');
              }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                typeFiltre === t.key ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        <DateRangePicker
          debut={dateDebut} fin={dateFin}
          onChange={(d, f) => { setDateDebut(d); setDateFin(f); }}
        />

        {/* Filtre fournisseur */}
        <div>
          <label className="block text-xs text-gray-500 mb-1">Fournisseur</label>
          <select
            value={fournisseurFiltre}
            onChange={e => setFournisseurFiltre(e.target.value)}
            className="w-full bg-gray-900 border border-gray-600 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-yellow-500"
          >
            <option value="">Tous les fournisseurs</option>
            {fournisseursActifs.map(f => (
              <option key={f.id} value={f.id}>
                {f.nom}{f.specialite ? ` (${f.specialite})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Filtre statut — pertinent uniquement pour les commandes */}
        {typeFiltre !== 'appros' && (
          <div className="flex gap-2 flex-wrap items-center">
            <span className="text-xs text-gray-500">Statut :</span>
            {['tous', 'brouillon', 'envoye', 'recu', 'annule'].map(s => (
              <button key={s} onClick={() => setStatut(s)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  statut === s ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}>
                {s === 'tous' ? 'Tous' : (STATUTS[s]?.label || s)}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : itemsAffiches.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-600 px-6 text-center">
          <FileText size={36} className="mb-3 opacity-40" />
          <p className="mb-2">Aucun élément pour cette période</p>
          <p className="text-xs text-gray-500 max-w-md">
            💡 Le filtre est sur <span className="text-yellow-500 font-medium">Aujourd'hui</span> par défaut.
            Pour voir l'historique, clique sur <span className="text-yellow-500 font-medium">7 jours</span>,{' '}
            <span className="text-yellow-500 font-medium">30 jours</span>, <span className="text-yellow-500 font-medium">Ce mois</span> ou{' '}
            <span className="text-yellow-500 font-medium">Mois dernier</span> dans les pills de date ci-dessus.
          </p>
        </div>
      ) : (
        <div className="bg-gray-800/50 border border-gray-700 rounded-2xl overflow-auto max-h-[70vh]">
          <table className="w-full text-sm min-w-[1000px]">
            <thead className="bg-gray-900 text-xs text-gray-400 uppercase tracking-wide sticky top-0 z-10">
              <tr>
                <th className="px-3 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Numéro</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Fournisseur</th>
                <th className="px-4 py-3 text-left">Produit(s)</th>
                <th className="px-4 py-3 text-center">Articles</th>
                <th className="px-4 py-3 text-right">Prix unitaire</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-center">Statut</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {itemsAffiches.map(it => {
                const isAppro = it._type === 'appro';
                const cle = `${it._type}-${it.id}`;
                const ouvert = expanded === cle;
                const lignesDetail = detailLignes[cle];
                return (
                  <React.Fragment key={cle}>
                  <tr onClick={() => toggleDetail(it)} className="hover:bg-gray-700/20 transition-colors cursor-pointer">
                    <td className="px-3 py-3">
                      <span className="text-gray-500 mr-1.5">{ouvert ? '▾' : '▸'}</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                        isAppro ? 'bg-orange-700/40 text-orange-300' : 'bg-yellow-700/40 text-yellow-300'
                      }`}>
                        {isAppro ? 'Appro direct' : 'Commande'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-yellow-400 whitespace-nowrap">{it.numero}</td>
                    <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                      <div>{new Date(it._date).toLocaleDateString('fr-FR')}</div>
                      {!isAppro && it.date_livraison_prevue && (
                        <div className="text-xs text-gray-600 flex items-center gap-1">
                          <Clock size={10} /> liv. {new Date(it.date_livraison_prevue).toLocaleDateString('fr-FR')}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-200">{it.fournisseur_nom}</td>
                    <td className="px-4 py-3 text-gray-300 max-w-[220px] truncate" title={it.produits_noms || ''}>
                      {it.produits_noms || '—'}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-300">
                      {it.nb_lignes} <span className="text-gray-600">·</span> {it.nb_articles} pcs
                    </td>
                    <td className="px-4 py-3 text-right text-gray-300 whitespace-nowrap">
                      {it.nb_articles > 0 ? formatGNF(Math.round(it.cout_total / it.nb_articles)) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-white font-bold whitespace-nowrap">
                      {formatGNF(it.cout_total)}
                    </td>
                    <td className="px-4 py-3 text-center"><StatutBadge statut={it.statut} /></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                      {isAppro ? (
                        // Approvisionnement direct : déjà reçu. Le propriétaire/admin
                        // peut le CORRIGER (erreur de prix/quantité).
                        <div className="flex items-center justify-end gap-2">
                          <span className="text-xs text-gray-500 italic">Stock entré</span>
                          {peutCorriger && (
                            <button
                              onClick={() => ouvrirCorrectionAppro(it)}
                              className="p-1.5 text-gray-400 hover:text-amber-400 hover:bg-gray-700 rounded-lg transition-colors"
                              title="Corriger cet approvisionnement (prix/quantité)"
                            >
                              <Edit2 size={14} />
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => voirEtImprimer(it)}
                            className="p-1.5 text-gray-400 hover:text-yellow-400 hover:bg-gray-700 rounded-lg transition-colors"
                            title="Voir / Imprimer / PDF"
                          >
                            <Printer size={14} />
                          </button>
                          <button
                            onClick={() => { setBonEdit(it); setModalCreer(true); }}
                            className="p-1.5 text-gray-400 hover:text-yellow-400 hover:bg-gray-700 rounded-lg transition-colors"
                            title={it.statut === 'brouillon' ? 'Modifier' : 'Voir détails'}
                          >
                            {it.statut === 'brouillon' ? <Edit2 size={14} /> : <Eye size={14} />}
                          </button>
                          {it.statut === 'brouillon' && (
                            <button
                              onClick={() => marquerEnvoye(it)}
                              className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg transition-colors"
                              title="Marquer envoyé au fournisseur"
                            >
                              <Send size={14} />
                            </button>
                          )}
                          {it.statut === 'envoye' && (
                            <button
                              onClick={() => marquerRecu(it)}
                              className="p-1.5 text-gray-400 hover:text-green-400 hover:bg-gray-700 rounded-lg transition-colors"
                              title="Marquer comme reçu (crée l'appro)"
                            >
                              <PackageCheck size={14} />
                            </button>
                          )}
                          {['brouillon', 'envoye'].includes(it.statut) && (
                            <button
                              onClick={() => annuler(it)}
                              className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg transition-colors"
                              title="Annuler"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  {ouvert && (
                    <tr className="bg-gray-900/40">
                      <td colSpan={10} className="px-6 pb-3 pt-1">
                        {lignesDetail === 'loading' ? (
                          <p className="text-xs text-gray-500 py-2">Chargement du détail…</p>
                        ) : !lignesDetail || lignesDetail.length === 0 ? (
                          <p className="text-xs text-gray-500 py-2">Aucun détail disponible.</p>
                        ) : (
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-gray-500 text-left">
                                <th className="py-1 pr-3">Produit</th>
                                <th className="py-1 pr-3 text-right">Qté</th>
                                <th className="py-1 pr-3 text-right">Prix unitaire</th>
                                <th className="py-1 text-right">Sous-total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {lignesDetail.map((l, i) => {
                                const pu = Number(isAppro ? l.prix_achat_unitaire : l.prix_unitaire) || 0;
                                const q = Number(l.quantite) || 0;
                                return (
                                  <tr key={i} className="border-t border-gray-700/40">
                                    <td className="py-1 pr-3 text-gray-200">
                                      {l.produit_nom}{l.modele ? ` · ${l.modele}` : ''}{l.couleur ? ` ${l.couleur}` : ''}{l.taille ? ` ${l.taille}` : ''}
                                    </td>
                                    <td className="py-1 pr-3 text-right text-gray-300">{q}</td>
                                    <td className="py-1 pr-3 text-right text-gray-300">{formatGNF(pu)}</td>
                                    <td className="py-1 text-right text-white">{formatGNF(pu * q)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
            className="px-4 py-2 bg-gray-800 text-gray-300 rounded-xl disabled:opacity-40 text-sm hover:bg-gray-700">← Préc.</button>
          <span className="px-4 py-2 text-gray-400 text-sm">{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}
            className="px-4 py-2 bg-gray-800 text-gray-300 rounded-xl disabled:opacity-40 text-sm hover:bg-gray-700">Suiv. →</button>
        </div>
      )}

      {/* Modal création/édition */}
      {modalCreer && (
        <FormBonCommande
          bon={bonEdit}
          onClose={() => { setModalCreer(false); setBonEdit(null); }}
          onSuccess={() => { setModalCreer(false); setBonEdit(null); charger(); }}
        />
      )}

      {/* Modal voir / imprimer / PDF / email / WhatsApp */}
      {bonImpression && (
        <ModalImprimerBon
          bon={bonImpression}
          onClose={() => setBonImpression(null)}
        />
      )}

      {/* Modal Approvisionnement direct — étape 1 : choisir le fournisseur */}
      {modalApproDirect && !fournisseurAppro && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white">Approvisionnement direct — étape 1/2</h2>
              <button onClick={() => setModalApproDirect(false)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Pour enregistrer un approvisionnement sans commande préalable
              (la marchandise est déjà arrivée). Met à jour stock + caisse immédiatement.
            </p>
            <label className="block text-xs text-gray-400 mb-1.5">Fournisseur</label>
            <select
              value=""
              onChange={(e) => {
                const f = fournisseursActifs.find(x => x.id === e.target.value);
                if (f) setFournisseurAppro(f);
              }}
              className="w-full bg-gray-800 border border-gray-600 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500"
            >
              <option value="">— Sélectionner un fournisseur —</option>
              {fournisseursActifs.map(f => (
                <option key={f.id} value={f.id}>
                  {f.nom}{f.specialite ? ` (${f.specialite})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Modal Approvisionnement direct — étape 2 : formulaire complet */}
      {modalApproDirect && fournisseurAppro && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-700">
              <h2 className="text-lg font-bold text-white">
                Approvisionnement direct — {fournisseurAppro.nom}
              </h2>
              <button
                onClick={() => { setModalApproDirect(false); setFournisseurAppro(null); }}
                className="text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5">
              <FormApprovisionnement
                fournisseur={fournisseurAppro}
                onSuccess={() => {
                  toast.success('Approvisionnement enregistré');
                  setModalApproDirect(false);
                  setFournisseurAppro(null);
                  charger();
                }}
                onCancel={() => { setModalApproDirect(false); setFournisseurAppro(null); }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal CORRECTION d'un approvisionnement existant */}
      {apprEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-700">
              <h2 className="text-lg font-bold text-white">
                Corriger l'approvisionnement — {apprEdit.fournisseur_nom || ''}
              </h2>
              <button onClick={() => setApprEdit(null)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="p-5">
              <FormApprovisionnement
                fournisseur={{ id: apprEdit.fournisseur_id, nom: apprEdit.fournisseur_nom }}
                approEdit={apprEdit}
                onSuccess={() => {
                  toast.success('Approvisionnement corrigé');
                  setApprEdit(null);
                  charger();
                }}
                onCancel={() => setApprEdit(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
